/**
 * Order History Page
 *
 * 주문 이력 페이지
 * - 카피 트레이딩으로 생성된 주문 목록
 * - 필터링 (심볼, 상태, 날짜 범위)
 * - 페이지네이션
 * - 주문 상세 정보 표시
 *
 * @see docs/COPY_TRADING_FRONTEND_PLAN.md
 */

import React, { useState, useEffect, useContext, useMemo, useCallback } from 'react';
import styled from 'styled-components';
import { Table, Select, DatePicker, Tag, Spin, Button, Space, message } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { ReloadOutlined, FilterOutlined } from '@ant-design/icons';
import dayjs, { Dayjs } from 'dayjs';
import { useTranslation } from 'react-i18next';
import { ThemeContext } from '@/theme/ThemeProvider';
import { useIsTablet } from '@utils/CalVW.tsx';
import { formatDecimal } from '@utils/formatDecimal';

import { getMyCopyOrders } from '../../api/copyTrading';
import type { CopyOrder, OrderStatus } from '../../../types/copyTrading';

const { RangePicker } = DatePicker;
const { Option } = Select;

export function OrderHistoryPage() {
  const { t } = useTranslation();
  const isTablet = useIsTablet();
  const { isDarkMode } = useContext(ThemeContext);

  // ==========================================
  // State
  // ==========================================

  const [orders, setOrders] = useState<CopyOrder[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [total, setTotal] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);

  // 필터 상태
  const [selectedSymbol, setSelectedSymbol] = useState<string | undefined>(undefined);
  const [selectedStatus, setSelectedStatus] = useState<OrderStatus | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[Dayjs | null, Dayjs | null] | null>(null);

  // ==========================================
  // Effects
  // ==========================================

  useEffect(() => {
    fetchOrders();
  }, [currentPage, pageSize, selectedSymbol, selectedStatus, dateRange]);

  // ==========================================
  // API Calls
  // ==========================================

  const fetchOrders = async () => {
    setIsLoading(true);

    try {
      const response = await getMyCopyOrders({
        page: currentPage,
        limit: pageSize,
      });

      console.log('[OrderHistoryPage] API Response:', response);

      if (response.success) {
        setOrders(response.data?.items || []);
        setTotal(response.data?.pagination?.total || 0);
      }
    } catch (error: any) {
      console.error('[OrderHistoryPage] fetchOrders error:', error);
      message.error(error.response?.data?.error?.message || 'Failed to fetch orders');
    } finally {
      setIsLoading(false);
    }
  };

  // ==========================================
  // Handlers (메모이제이션)
  // ==========================================

  const handleRefresh = useCallback(() => {
    fetchOrders();
  }, [fetchOrders]);

  const handleResetFilters = useCallback(() => {
    setSelectedSymbol(undefined);
    setSelectedStatus(undefined);
    setDateRange(null);
    setCurrentPage(1);
  }, []);

  // ==========================================
  // Table Columns
  // ==========================================

  const columns: ColumnsType<CopyOrder> = useMemo(() => [
    {
      title: t('time'),
      dataIndex: 'createdAt',
      key: 'createdAt',
      width: 150,
      render: (createdAt) => {
        if (!createdAt) return '-';
        const date = dayjs(createdAt);
        if (!date.isValid()) return '-';
        return (
          <div>
            <div>{date.format('YYYY-MM-DD')}</div>
            <div style={{ fontSize: 12, opacity: 0.7 }}>
              {date.format('HH:mm:ss')}
            </div>
          </div>
        );
      },
    },
    {
      title: t('symbol'),
      dataIndex: 'symbol',
      key: 'symbol',
      width: 120,
      render: (symbol) => <strong>{symbol}</strong>,
    },
    {
      title: t('side'),
      dataIndex: 'longShort',
      key: 'longShort',
      width: 80,
      render: (longShort: 'L' | 'S') => {
        const displaySide = longShort === 'L' ? 'LONG' : 'SHORT';
        const color = longShort === 'L' ? 'green' : 'red';
        return <Tag color={color}>{displaySide}</Tag>;
      },
    },
    {
      title: t('type'),
      dataIndex: 'limitMarket',
      key: 'limitMarket',
      width: 100,
      render: (limitMarket: 'L' | 'M') => {
        const displayType = limitMarket === 'L' ? 'LIMIT' : 'MARKET';
        const color = limitMarket === 'L' ? 'blue' : 'orange';
        return <Tag color={color}>{displayType}</Tag>;
      },
    },
    {
      title: t('quantity'),
      dataIndex: 'amount',
      key: 'amount',
      width: 120,
      align: 'right',
      render: (quantity) => quantity ? formatDecimal(quantity, 4) : '-',
    },
    {
      title: t('price'),
      dataIndex: 'orderPrice',
      key: 'orderPrice',
      width: 120,
      align: 'right',
      render: (price, record) => {
        if (record.type === 'MARKET') {
          return <span style={{ opacity: 0.5 }}>{t('market')}</span>;
        }
        return formatDecimal(price, 2);
      },
    },
    {
      title: t('leverage'),
      dataIndex: 'leverage',
      key: 'leverage',
      width: 100,
      align: 'center',
      render: (leverage) => `${leverage || 1}x`,
    },
    {
      title: t('status'),
      dataIndex: 'status',
      key: 'status',
      width: 140,
      align: 'center',
      render: (status: OrderStatus | number) => {
        // status가 숫자로 올 수 있음 (1=PENDING, 2=FILLED 등)
        const statusConfig = {
          NEW: { text: t('new'), color: 'blue' },
          FILLED: { text: t('filled'), color: 'green' },
          PARTIALLY_FILLED: { text: t('partial'), color: 'orange' },
          CANCELED: { text: t('canceled'), color: 'default' },
          REJECTED: { text: t('rejected'), color: 'red' },
          1: { text: 'Pending', color: 'blue' },
          2: { text: 'Filled', color: 'green' },
          3: { text: 'Canceled', color: 'default' },
        };
        const config = statusConfig[status] || { text: String(status), color: 'default' };
        return <Tag color={config.color}>{config.text}</Tag>;
      },
    },
  ], [t]);

  // 심볼 목록 (실제로는 API에서 가져올 수 있음)
  const symbolOptions = ['BTCUSDT', 'ETHUSDT', 'SOLUSDT', 'BNBUSDT', 'ADAUSDT'];

  // ==========================================
  // Render
  // ==========================================

  return (
      <PageWrapper>
      <CompactHeader isDark={isDarkMode}>
        <HeaderLeft>
          <HeaderTitle>{t('orderHistoryTitle')}</HeaderTitle>
          <HeaderDescription>{t('orderHistoryDesc')}</HeaderDescription>
        </HeaderLeft>

        <StatsRow>
          <StatItem>
            <StatLabel>{t('totalOrders')}</StatLabel>
            <StatValue>{total}</StatValue>
          </StatItem>
          <StatDivider />
          <StatItem>
            <Button icon={<ReloadOutlined />} onClick={handleRefresh}>
              {t('refresh')}
            </Button>
          </StatItem>
        </StatsRow>
      </CompactHeader>

      {/* 필터 섹션 */}
      <FilterSection isDark={isDarkMode}>
        <FilterRow>
          <FilterItem>
            <FilterLabel>{t('symbol')}</FilterLabel>
            <Select
              style={{ width: 150 }}
              placeholder={t('all')}
              allowClear
              value={selectedSymbol}
              onChange={setSelectedSymbol}
            >
              {symbolOptions.map((symbol) => (
                <Option key={symbol} value={symbol}>
                  {symbol}
                </Option>
              ))}
            </Select>
          </FilterItem>

          <FilterItem>
            <FilterLabel>{t('status')}</FilterLabel>
            <Select
              style={{ width: 150 }}
              placeholder={t('all')}
              allowClear
              value={selectedStatus}
              onChange={setSelectedStatus}
            >
              <Option value="NEW">{t('new')}</Option>
              <Option value="FILLED">{t('filled')}</Option>
              <Option value="PARTIALLY_FILLED">{t('partial')}</Option>
              <Option value="CANCELED">{t('canceled')}</Option>
              <Option value="REJECTED">{t('rejected')}</Option>
            </Select>
          </FilterItem>

          <FilterItem>
            <FilterLabel>{t('period')}</FilterLabel>
            <RangePicker
              value={dateRange}
              onChange={setDateRange}
              format="YYYY-MM-DD"
              style={{ width: 280 }}
            />
          </FilterItem>

          <Button
            icon={<FilterOutlined />}
            onClick={handleResetFilters}
            style={{ marginTop: isTablet ? 0 : 22 }}
          >
            {t('filterReset')}
          </Button>
        </FilterRow>
      </FilterSection>

      {/* 테이블 */}
      <TableSection isDark={isDarkMode}>
        <Table
          columns={columns}
          dataSource={orders}
          rowKey={(record) => record.id}
          loading={isLoading}
          scroll={{ x: 'max-content' }}
          pagination={{
            current: currentPage,
            pageSize: pageSize,
            total: total,
            showSizeChanger: true,
            showTotal: (total) => `${t('total')} ${total}${t('items')}`,
            pageSizeOptions: ['10', '20', '50', '100'],
            onChange: (page, size) => {
              setCurrentPage(page);
              setPageSize(size);
            },
          }}
        />
      </TableSection>

      {/* 안내 문구 */}
      {!isLoading && (orders || []).length === 0 && (
        <EmptyState isDark={isDarkMode}>
          <EmptyIcon>📋</EmptyIcon>
          <p style={{ fontSize: 18, fontWeight: 600, marginBottom: 8 }}>{t('noOrderHistory')}</p>
          <p style={{ fontSize: 14, opacity: 0.7 }}>
            {t('startFollowing')}
          </p>
        </EmptyState>
      )}
      </PageWrapper>
  );
}

// ==========================================
// Styled Components
// ==========================================

const PageWrapper = styled.div`
  display: flex;
  max-width: 1200px;
  width: 100%;
  flex-direction: column;
  align-items: flex-start;
  gap: 0;

  @media (max-width: 1240px) {
    padding: 20px;
  }
`;

const CompactHeader = styled.div<{ isDark: boolean }>`
  width: 100%;
  display: grid;
  grid-template-columns: minmax(200px, 1fr) auto;
  align-items: center;
  gap: 24px;
  margin-bottom: 20px;
  padding: 20px 24px;
  box-sizing: border-box;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.05) 0%, rgba(20, 17, 34, 0.05) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(255, 255, 255, 1) 100%)'
  };
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
  }
`;

const HeaderLeft = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const HeaderTitle = styled.h1`
  font-size: 20px;
  font-weight: 700;
  margin: 0;
  color: ${({ theme }) => theme.text};
`;

const HeaderDescription = styled.p`
  margin: 0;
  font-size: 13px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
`;

const StatsRow = styled.div`
  display: grid;
  grid-template-columns: auto auto auto;
  align-items: center;
  gap: 20px;
  justify-self: end;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 12px;
    width: 100%;
    justify-self: stretch;
  }
`;

const StatItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-end;
  gap: 2px;
  min-width: 110px;

  @media (max-width: 768px) {
    align-items: center;
    min-width: unset;
  }
`;

const StatLabel = styled.div`
  font-size: 11px;
  color: ${({ theme }) => theme.text};
  opacity: 0.6;
  text-transform: uppercase;
  letter-spacing: 0.5px;
`;

const StatValue = styled.div`
  font-size: 16px;
  font-weight: 700;
  color: ${({ theme }) => theme.text};
  white-space: nowrap;
`;

const StatDivider = styled.div`
  width: 1px;
  height: 32px;
  background-color: ${({ theme }) => theme.text};
  opacity: 0.1;

  @media (max-width: 768px) {
    display: none;
  }
`;

const FilterSection = styled.div<{ isDark: boolean }>`
  width: 100%;
  margin-bottom: 24px;
  padding: 20px;
  box-sizing: border-box;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const FilterRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  align-items: flex-end;
`;

const FilterItem = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const FilterLabel = styled.div`
  font-size: 14px;
  font-weight: 600;
  color: ${({ theme }) => theme.text};
`;

const TableSection = styled.div<{ isDark: boolean }>`
  width: 100%;
  background-color: ${({ isDark }) => (isDark ? '#1a1a1a' : '#fff')};
  border-radius: 12px;
  padding: 20px;
  box-sizing: border-box;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #e0e0e0')};
`;

const EmptyState = styled.div<{ isDark: boolean }>`
  width: 100%;
  margin-top: 24px;
  padding: 80px 20px;
  box-sizing: border-box;
  text-align: center;
  background: ${({ isDark }) =>
    isDark
      ? 'linear-gradient(135deg, rgba(255, 145, 49, 0.03) 0%, rgba(20, 17, 34, 0.03) 100%), #1a1a1a'
      : 'linear-gradient(135deg, rgba(255, 145, 49, 0.02) 0%, rgba(255, 255, 255, 1) 100%)'
  };
  border-radius: 12px;
  border: ${({ isDark }) => (isDark ? '1px solid #333' : '1px solid #f0f0f0')};
  color: ${({ theme }) => theme.text};

  p {
    margin: 8px 0;
  }
`;

const EmptyIcon = styled.div`
  font-size: 48px;
  margin-bottom: 16px;
`;
